import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";

export type TemplateStepCommand =
  | "duplicate"
  | "move-up"
  | "move-down"
  | "delete"
  | "insert-top"
  | "insert-bottom"
  | "insert-at";

const labels: Record<
  TemplateStepCommand,
  { title: string; action: string; description: (name: string) => string }
> = {
  duplicate: {
    title: "Duplicate section",
    action: "Confirm duplicate",
    description: (name) => `Duplicate “${name}” directly after this section?`,
  },
  "move-up": {
    title: "Move section up",
    action: "Confirm move up",
    description: (name) => `Move “${name}” one position up?`,
  },
  "move-down": {
    title: "Move section down",
    action: "Confirm move down",
    description: (name) => `Move “${name}” one position down?`,
  },
  delete: {
    title: "Delete section",
    action: "Confirm delete",
    description: (name) =>
      `Delete “${name}” and its variables? This cannot be undone.`,
  },
  "insert-top": {
    title: "Insert section at top",
    action: "Confirm insert at top",
    description: (name) => `Insert “${name}” at the top of this template?`,
  },
  "insert-bottom": {
    title: "Insert section at bottom",
    action: "Confirm insert at bottom",
    description: (name) => `Insert “${name}” at the bottom of this template?`,
  },
  "insert-at": {
    title: "Insert section",
    action: "Confirm insert",
    description: (name) => `Insert “${name}” at the selected position?`,
  },
};

type TemplateStepCommandDialogProps = {
  open: boolean;
  command: TemplateStepCommand;
  sectionName: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<unknown>;
};

export function TemplateStepCommandDialog({
  open,
  command,
  sectionName,
  isPending,
  onOpenChange,
  onConfirm,
}: TemplateStepCommandDialogProps) {
  const [error, setError] = useState<string>();
  const copy = labels[command];

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setError(undefined);
    onOpenChange(nextOpen);
  };

  const confirm = async () => {
    setError(undefined);
    try {
      await onConfirm();
      handleOpenChange(false);
    } catch (commandError) {
      setError(
        commandError instanceof Error
          ? commandError.message
          : "Could not update section",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description(sectionName)}</DialogDescription>
        </DialogHeader>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={command === "delete" ? "destructive" : "default"}
            disabled={isPending}
            onClick={() => void confirm()}
          >
            {isPending ? "Saving…" : copy.action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
